import {InjectionToken} from '@angular/core'
import {DataEmbeddedService} from '@service/data-embedding/data-embedding.service'
import {DataService} from '@service/data.service'
import type {EmbeddedClientService} from '@service/embeddingService/embedded-client.service'

/**
 * Factory que resuelve la implementación adecuada de `DataService`.
 *
 * @description
 * En modo embebido devuelve `DataEmbeddedService`, que delega las
 * operaciones de datos al host a través del puente de mensajería. En
 * modo standalone devuelve `DataService`, responsable de las
 * instancias PouchDB locales y de la abstracción offline-first.
 *
 * `DataService` es el único punto del código autorizado a importar
 * PouchDB (regla de arquitectura hexagonal), así que este provider
 * centraliza su instanciación.
 *
 * @param embeddedClientService Cliente que detecta si la app corre embebida.
 * @returns Instancia activa de `DataService` (normal o embebida).
 */
export function providerDataServiceFactory(embeddedClientService: EmbeddedClientService) {
  return embeddedClientService.isEmbeddedFunction() ? new DataEmbeddedService() : new DataService()
}

/**
 * `InjectionToken` que referencia la instancia activa de `DataService`.
 *
 * @description
 * Registrado en `SERVICE_PROVIDERS` junto con
 * {@link providerDataServiceFactory}. Recuerda: `DataService` **no** debe
 * inyectarse en componentes ni en servicios de dominio; usa `ApiService`
 * u otros servicios de dominio en su lugar.
 */
export const DataServiceToken = new InjectionToken<DataService>('DataService')
